import { styled } from "goober";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { isSqliteMediaType, previewKind } from "../../shared/attachmentPolicy";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { isVolumeFilePath } from "../lib/apiPaths";
import { base64ToUint8Array } from "../lib/base64";
import { playClick } from "../lib/sounds";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { Button } from "./Button";
import { useCodeEditorSearch } from "./CodeEditor/useCodeEditorSearch";
import { FileRender, type FileRenderControl } from "./FileRender";
import { Icon, type IconName } from "./Icon";
import { MenuItem } from "./MenuItem";
import { Modal } from "./Modal";
import { Text } from "./Text";
import {
  type Align,
  getLastTooltipCloseTime,
  Tooltip,
  getPositionStyles,
  setLastTooltipCloseTime,
  TOOLTIP_DELAY,
  type Vertical,
  WARM_PERIOD,
} from "./Tooltip";
import { WavyLine } from "./WavyLine";

const PREVIEW_MAX_WIDTH = 260;
const PREVIEW_MAX_HEIGHT = 180;
const MODAL_MAX_WIDTH = 900;
const MODAL_MAX_HEIGHT = 620;
const SKELETON_WIDTH = 100;
const SKELETON_HEIGHT = 70;
const TEXT_SNIPPET_MAX_CHARS = 4096;

type Props = {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  url: string | null;
  inlineData: string | null;
  onRemove?: () => void;
  children: React.ReactNode;
};

type LoadStatus = "idle" | "loading" | "ready" | "error";

const Container = styled("div")`
  display: inline-flex;
`;

const Trigger = styled("div")`
  display: inline-flex;
  min-width: 0;
`;

const PopoverCard = styled(AnchoredPopover)`
  background: var(--surface-primary);
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-high);
  border-radius: 16px;
  padding: 8px;
  line-height: 0;
  z-index: ${zIndexes.tooltip};
  pointer-events: none;
`;

const ContextMenu = styled("div")`
  position: fixed;
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  border-radius: 12px;
  padding: 4px;
  z-index: ${zIndexes.contextMenu};
  min-width: 160px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const PreviewContainer = styled("div")`
  border-radius: 12px;
  overflow: hidden;
  outline: 0.5px solid var(--border-alpha);
  background: var(--alpha4);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  max-width: ${PREVIEW_MAX_WIDTH}px;
  max-height: ${PREVIEW_MAX_HEIGHT}px;
`;

const ModalContainer = styled("div")`
  display: flex;
  flex-direction: column;
  width: min(${MODAL_MAX_WIDTH}px, calc(100vw - 48px));
  height: min(${MODAL_MAX_HEIGHT}px, calc(100vh - 48px));
  overflow: hidden;

  &[data-kind="sqlite"] {
    width: min(1100px, calc(100vw - 48px));
    height: min(760px, calc(100vh - 48px));
  }

  &[data-kind="csv"] {
    width: min(1100px, calc(100vw - 48px));
    height: min(760px, calc(100vh - 48px));
  }
`;

const ModalHeader = styled("div")`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-bottom: 0.5px solid var(--border-alpha);
`;

const ModalIconBadge = styled("div")`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: var(--alpha4);
`;

const ModalTitle = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
`;

const ModalBody = styled("div")`
  flex: 1;
  min-height: 0;
  padding: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: var(--surface-primary);

  &[data-kind="sqlite"] {
    background: var(--surface-secondary);
    align-items: stretch;
    justify-content: stretch;
  }
`;

const ModalPreviewFrame = styled("div")`
  max-width: 100%;
  max-height: 100%;
  border-radius: 12px;
  overflow: auto;
  outline: 0.5px solid var(--border-alpha);
  background: var(--surface-primary);
  box-shadow: var(--elevation-low);
`;

const ModalCodeFrame = styled(ModalPreviewFrame)`
  width: 100%;
  height: 100%;
  background: var(--surface-secondary);
  box-shadow: none;
`;

const PreviewText = styled("pre")`
  margin: 0;
  padding: 0;
  font-family: var(--font-mono);
  font-size: 9px;
  line-height: 12px;
  color: var(--text-primary);
  white-space: pre;
  overflow: hidden;
  max-width: ${PREVIEW_MAX_WIDTH}px;
  max-height: ${PREVIEW_MAX_HEIGHT}px;
  box-sizing: border-box;
`;

const NoPreviewTile = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 4px;
  box-sizing: border-box;
  color: var(--text-secondary);
  max-width: ${PREVIEW_MAX_WIDTH}px;
`;

const NoPreviewRow = styled("div")`
  display: flex;
  justify-content: space-between;
  gap: 12px;
  min-width: 0;
`;

const ModalNoPreviewTile = styled(NoPreviewTile)`
  min-width: min(360px, calc(100vw - 96px));
  max-width: min(520px, calc(100vw - 96px));
  padding: 32px;
`;

const SkeletonBox = styled("div")`
  width: ${SKELETON_WIDTH}px;
  height: ${SKELETON_HEIGHT}px;
  background: var(--alpha8);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalSkeletonBox = styled(SkeletonBox)`
  width: 160px;
  height: 112px;
  border-radius: 12px;
`;

function Skeleton() {
  return (
    <SkeletonBox>
      <WavyLine size={16} opacity={0.4} />
    </SkeletonBox>
  );
}

function ModalSkeleton() {
  return (
    <ModalSkeletonBox>
      <WavyLine size={24} opacity={0.4} />
    </ModalSkeletonBox>
  );
}

function formatSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function mediaTypeLabel(mediaType: string): string {
  if (isSqliteMediaType(mediaType)) return "SQLite database";

  switch (mediaType) {
    case "application/pdf":
      return "PDF document";
    case "application/zip":
    case "application/x-zip-compressed":
      return "ZIP archive";
    case "application/json":
      return "JSON";
    case "application/msword":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
      return "Word document";
    case "application/vnd.ms-excel":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
      return "Excel spreadsheet";
    case "image/png":
      return "PNG image";
    case "image/jpeg":
      return "JPEG image";
    case "image/gif":
      return "GIF image";
    case "image/webp":
      return "WebP image";
    case "text/plain":
      return "Plain text";
    case "text/markdown":
      return "Markdown";
    case "text/csv":
      return "CSV";
    case "text/html":
      return "HTML";
    default:
      return mediaType;
  }
}

function isVolumeFileUrl(url: URL) {
  return isVolumeFilePath(url.pathname);
}

function downloadAttachment({
  fileName,
  mediaType,
  url,
  inlineData,
}: Pick<Props, "fileName" | "mediaType" | "url" | "inlineData">) {
  if (inlineData) {
    const blob = new Blob([base64ToUint8Array(inlineData)], {
      type: mediaType,
    });
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(blobUrl);
    return;
  }

  if (!url) return;
  const downloadUrl = new URL(url, location.href);
  if (isVolumeFileUrl(downloadUrl)) {
    downloadUrl.searchParams.set("download", "1");
    if (isSqliteMediaType(mediaType)) {
      downloadUrl.searchParams.set("sqlite", "1");
    }
  }
  const a = document.createElement("a");
  a.href = downloadUrl.href;
  a.download = fileName;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.click();
}

function openAttachment(url: string | null) {
  if (!url) return;
  window.open(url, "_blank", "noopener,noreferrer");
}

function isBrowserViewable(mediaType: string): boolean {
  return mediaType === "application/pdf";
}

function attachmentIcon(mediaType: string): IconName {
  if (isSqliteMediaType(mediaType)) return "database";
  if (mediaType === "text/csv") return "table";
  return mediaType === "application/pdf" ? "file-scan" : "paperclip";
}

export function attachmentActionIcon(mediaType: string): IconName {
  if (previewKind(mediaType) !== "fallback") return "maximize-2";
  if (isBrowserViewable(mediaType)) return "arrow-up-right";
  return "circle-arrow-down";
}

function useImagePreload(enabled: boolean, src: string | null) {
  const [status, setStatus] = useState<LoadStatus>("idle");
  const [image, setImage] = useState<HTMLImageElement | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || !src || startedRef.current) return;
    startedRef.current = true;
    setStatus("loading");
    const img = new Image();
    img.crossOrigin = "use-credentials";
    img.style.display = "block";
    img.style.maxWidth = `${PREVIEW_MAX_WIDTH}px`;
    img.style.maxHeight = `${PREVIEW_MAX_HEIGHT}px`;
    img.style.width = "auto";
    img.style.height = "auto";
    img.onload = () => {
      setImage(img);
      setStatus("ready");
    };
    img.onerror = () => setStatus("error");
    img.src = src;
  }, [enabled, src]);

  return { status, image };
}

function useTextPreload(
  enabled: boolean,
  url: string | null,
  inlineData: string | null,
  maxChars = TEXT_SNIPPET_MAX_CHARS
) {
  const [status, setStatus] = useState<LoadStatus>("idle");
  const [snippet, setSnippet] = useState<string | null>(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (!enabled || startedRef.current) return;
    startedRef.current = true;
    setStatus("loading");

    if (inlineData) {
      try {
        const text = new TextDecoder().decode(base64ToUint8Array(inlineData));
        setSnippet(text.slice(0, maxChars));
        setStatus("ready");
      } catch {
        setStatus("error");
      }
      return;
    }

    if (!url) {
      setStatus("error");
      return;
    }

    fetch(url, { credentials: "include" })
      .then((r) =>
        r.ok ? r.text() : Promise.reject(new Error(String(r.status)))
      )
      .then((text) => {
        setSnippet(text.slice(0, maxChars));
        setStatus("ready");
      })
      .catch(() => {
        setStatus("error");
      });
  }, [enabled, url, inlineData, maxChars]);

  return { status, snippet };
}

function NoPreview({
  fileName,
  mediaType,
  sizeBytes,
  modal = false,
}: {
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  modal?: boolean;
}) {
  const Tile = modal ? ModalNoPreviewTile : NoPreviewTile;
  return (
    <Tile>
      <NoPreviewRow>
        <Text variant="text-small-md" color="var(--text-primary)">
          File:
        </Text>
        <Text variant="text-small" color="var(--text-secondary)" ellipsis>
          {fileName}
        </Text>
      </NoPreviewRow>
      <NoPreviewRow>
        <Text variant="text-small-md" color="var(--text-primary)">
          Type:
        </Text>
        <Text variant="text-small" color="var(--text-secondary)" ellipsis>
          {mediaTypeLabel(mediaType)}
        </Text>
      </NoPreviewRow>
      <NoPreviewRow>
        <Text variant="text-small-md" color="var(--text-primary)">
          Size:
        </Text>
        <Text variant="text-small" color="var(--text-secondary)">
          {formatSize(sizeBytes)}
        </Text>
      </NoPreviewRow>
    </Tile>
  );
}

function PreloadedImage({ image }: { image: HTMLImageElement }) {
  const hostRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const host = hostRef.current;
    if (host) host.replaceChildren(image);
  }, [image]);
  return <div ref={hostRef} style={{ display: "inline-flex" }} />;
}

const ModalImage = styled("img")`
  display: block;
  max-width: 100%;
  max-height: 100%;
`;

function ModalImageBody({
  src,
  fallback,
}: {
  src: string;
  fallback: React.ReactNode;
}) {
  const [status, setStatus] = useState<LoadStatus>("loading");
  if (status === "error") return fallback;
  return (
    <>
      {status === "loading" && <ModalSkeleton />}
      <ModalImage
        src={src}
        crossOrigin="use-credentials"
        style={status === "ready" ? undefined : { display: "none" }}
        onLoad={() => setStatus("ready")}
        onError={() => setStatus("error")}
      />
    </>
  );
}

function AttachmentPreviewModal({
  fileName,
  mediaType,
  sizeBytes,
  url,
  inlineData,
  open,
  onClose,
}: Omit<Props, "children" | "onRemove"> & {
  open: boolean;
  onClose: () => void;
}) {
  const kind = previewKind(mediaType);
  const [controls, setControls] = useState<FileRenderControl[]>([]);
  const search = useCodeEditorSearch();
  // Un-uploaded chat attachments arrive as base64; a data: URL carries the
  // media type, so it fetches like any file URL.
  const renderUrl =
    url ?? (inlineData ? `data:${mediaType};base64,${inlineData}` : null);

  const openFile = () => {
    if (url && isBrowserViewable(mediaType)) openAttachment(url);
    else downloadAttachment({ fileName, mediaType, url, inlineData });
  };
  const opensInBrowser = url && isBrowserViewable(mediaType);

  const noPreview = (
    <NoPreview
      fileName={fileName}
      mediaType={mediaType}
      sizeBytes={sizeBytes}
      modal
    />
  );

  // FileRender's image renderer is the compact row whose Preview button opens
  // this modal, so the modal shows images itself; everything else renders
  // through the shared FileRender dispatch.
  let body: React.ReactNode;
  let codeFrame = false;
  if (!renderUrl) {
    body = noPreview;
  } else if (!open) {
    body = null;
  } else if (kind === "image") {
    body = <ModalImageBody src={renderUrl} fallback={noPreview} />;
  } else {
    codeFrame = true;
    body = (
      <FileRender
        url={renderUrl}
        fileName={fileName}
        sizeBytes={sizeBytes}
        onControls={setControls}
      />
    );
  }

  return (
    <Modal open={open} onClose={onClose}>
      <search.Provider>
        <ModalContainer data-kind={kind}>
          <ModalHeader>
            <ModalIconBadge>
              <Icon
                name={attachmentIcon(mediaType)}
                size={16}
                color="var(--text-primary)"
              />
            </ModalIconBadge>
            <ModalTitle>
              <Text variant="text-regular-md" ellipsis>
                {fileName}
              </Text>
              <Text variant="text-small" color="var(--text-secondary)">
                {formatSize(sizeBytes)}
              </Text>
            </ModalTitle>
            {controls.map((control) => (
              <Tooltip key={control.id} content={control.label}>
                <Button
                  type="button"
                  variant="secondary"
                  icon={
                    control.active && control.activeIcon
                      ? control.activeIcon
                      : control.icon
                  }
                  onClick={control.onToggle}
                />
              </Tooltip>
            ))}
            {search.toggleButton}
            {renderUrl != null && (
              <Button
                type="button"
                variant="secondary"
                icon={opensInBrowser ? "arrow-up-right" : "circle-arrow-down"}
                onClick={openFile}
              >
                {opensInBrowser ? "Open" : "Download"}
              </Button>
            )}
            <Button
              type="button"
              variant="secondary"
              icon="x"
              onClick={onClose}
            />
          </ModalHeader>
          {search.subheader}
          <ModalBody data-kind={kind}>
            {kind === "sqlite" ? (
              body
            ) : codeFrame ? (
              <ModalCodeFrame>{body}</ModalCodeFrame>
            ) : (
              <ModalPreviewFrame>{body}</ModalPreviewFrame>
            )}
          </ModalBody>
        </ModalContainer>
      </search.Provider>
    </Modal>
  );
}

export function FilePreviewModal({
  open,
  onClose,
  fileName,
  mediaType,
  sizeBytes,
  url,
}: {
  open: boolean;
  onClose: () => void;
  fileName: string;
  mediaType: string;
  sizeBytes: number;
  url: string;
}) {
  return (
    <AttachmentPreviewModal
      fileName={fileName}
      mediaType={mediaType}
      sizeBytes={sizeBytes}
      url={url}
      inlineData={null}
      open={open}
      onClose={onClose}
    />
  );
}

function AttachmentContextMenu({
  x,
  y,
  canPreview,
  hasSource,
  hasUrl,
  onPreview,
  onDownload,
  onOpen,
  onRemove,
  onClose,
}: {
  x: number;
  y: number;
  canPreview: boolean;
  hasSource: boolean;
  hasUrl: boolean;
  onPreview: () => void;
  onDownload: () => void;
  onOpen: () => void;
  onRemove?: () => void;
  onClose: () => void;
}) {
  const [containerRef, menuRef] = useMouseDownOutside(onClose);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const adjustedX = Math.min(x, window.innerWidth - 180);
  const adjustedY = Math.min(y, window.innerHeight - 180);

  const items: React.ReactNode[] = [];
  if (canPreview) {
    items.push(
      <MenuItem
        key="preview"
        onClick={() => {
          onPreview();
          onClose();
        }}
      >
        <Icon name="maximize-2" size={16} />
        <Text variant="text-regular">Preview</Text>
      </MenuItem>
    );
  }

  if (!canPreview && hasUrl) {
    items.push(
      <MenuItem
        key="open"
        onClick={() => {
          onOpen();
          onClose();
        }}
      >
        <Icon name="arrow-up-right" size={16} />
        <Text variant="text-regular">Open in new tab</Text>
      </MenuItem>
    );
  }

  if (hasSource) {
    items.push(
      <MenuItem
        key="download"
        onClick={() => {
          onDownload();
          onClose();
        }}
      >
        <Icon name="circle-arrow-down" size={16} />
        <Text variant="text-regular">Download</Text>
      </MenuItem>
    );
  }

  if (onRemove) {
    items.push(
      <MenuItem
        key="remove"
        onClick={() => {
          onRemove();
          onClose();
        }}
      >
        <Icon name="x" size={16} />
        <Text variant="text-regular">Remove</Text>
      </MenuItem>
    );
  }

  if (items.length === 0) return null;

  return createPortal(
    <div ref={containerRef} style={{ display: "contents" }}>
      <ContextMenu
        ref={menuRef}
        data-popover
        data-direction="down"
        style={{ left: adjustedX, top: adjustedY }}
      >
        {items}
      </ContextMenu>
    </div>,
    document.body
  );
}

export function AttachmentHoverPreview({
  fileName,
  mediaType,
  sizeBytes,
  url,
  inlineData,
  onRemove,
  children,
}: Props) {
  const [isVisible, setIsVisible] = useState(false);
  const [align, setAlign] = useState<Align>("center");
  const [vertical, setVertical] = useState<Vertical>("above");
  const [hoverStarted, setHoverStarted] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number }>();
  const triggerRef = useRef<HTMLDivElement>(null);
  const delayTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const kind = previewKind(mediaType);
  const hasSource = url !== null || inlineData !== null;
  const canPreview = hasSource && kind !== "fallback";
  const imageSrc =
    kind === "image"
      ? (url ?? (inlineData ? `data:${mediaType};base64,${inlineData}` : null))
      : null;

  const imagePreload = useImagePreload(
    hoverStarted && kind === "image" && hasSource,
    imageSrc
  );
  const textPreload = useTextPreload(
    hoverStarted && (kind === "text" || kind === "csv") && hasSource,
    url,
    inlineData
  );

  const showPopover = () => {
    if (triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;

      if (rect.left < 100) setAlign("left");
      else if (viewportWidth - rect.right < 100) setAlign("right");
      else setAlign("center");

      setVertical(rect.top < PREVIEW_MAX_HEIGHT + 40 ? "below" : "above");
    }
    setIsVisible(true);
  };

  const handleMouseEnter = () => {
    if (!hasSource) return;
    setHoverStarted(true);
    const isWarm = Date.now() - getLastTooltipCloseTime() < WARM_PERIOD;
    if (isWarm) showPopover();
    else delayTimerRef.current = setTimeout(showPopover, TOOLTIP_DELAY);
  };

  const handleMouseLeave = () => {
    if (delayTimerRef.current) {
      clearTimeout(delayTimerRef.current);
      delayTimerRef.current = null;
    }
    if (isVisible) setLastTooltipCloseTime(Date.now());
    setIsVisible(false);
  };

  const handleTriggerMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    playClick();
    if (canPreview) setModalOpen(true);
    else if (url && isBrowserViewable(mediaType)) openAttachment(url);
    else downloadAttachment({ fileName, mediaType, url, inlineData });
  };

  useEffect(() => {
    return () => {
      if (delayTimerRef.current) clearTimeout(delayTimerRef.current);
    };
  }, []);

  let body: React.ReactNode;
  if (!hasSource) {
    body = (
      <NoPreview
        fileName={fileName}
        mediaType={mediaType}
        sizeBytes={sizeBytes}
      />
    );
  } else if (kind === "image") {
    if (imagePreload.status === "ready" && imagePreload.image) {
      body = <PreloadedImage image={imagePreload.image} />;
    } else if (imagePreload.status === "error") {
      body = (
        <NoPreview
          fileName={fileName}
          mediaType={mediaType}
          sizeBytes={sizeBytes}
        />
      );
    } else {
      body = <Skeleton />;
    }
  } else if (kind === "text" || kind === "csv") {
    if (textPreload.status === "ready" && textPreload.snippet !== null) {
      body = <PreviewText>{textPreload.snippet}</PreviewText>;
    } else if (textPreload.status === "error") {
      body = (
        <NoPreview
          fileName={fileName}
          mediaType={mediaType}
          sizeBytes={sizeBytes}
        />
      );
    } else {
      body = <Skeleton />;
    }
  } else {
    body = (
      <NoPreview
        fileName={fileName}
        mediaType={mediaType}
        sizeBytes={sizeBytes}
      />
    );
  }

  return (
    <Container>
      <PopoverCard
        isOpen={isVisible}
        style={getPositionStyles(align, vertical)}
        trigger={(props) => (
          <Trigger
            ref={triggerRef}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseDown={onRemove ? undefined : handleTriggerMouseDown}
            onContextMenu={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (delayTimerRef.current) {
                clearTimeout(delayTimerRef.current);
                delayTimerRef.current = null;
              }
              setIsVisible(false);
              setContextMenu({ x: e.clientX, y: e.clientY });
            }}
            {...props}
          >
            {children}
          </Trigger>
        )}
      >
        <PreviewContainer>{body}</PreviewContainer>
      </PopoverCard>
      <AttachmentPreviewModal
        fileName={fileName}
        mediaType={mediaType}
        sizeBytes={sizeBytes}
        url={url}
        inlineData={inlineData}
        open={modalOpen}
        onClose={() => setModalOpen(false)}
      />
      {contextMenu && (
        <AttachmentContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          canPreview={canPreview}
          hasSource={hasSource}
          hasUrl={url !== null}
          onPreview={() => setModalOpen(true)}
          onDownload={() =>
            downloadAttachment({ fileName, mediaType, url, inlineData })
          }
          onOpen={() => openAttachment(url)}
          onRemove={onRemove}
          onClose={() => setContextMenu(undefined)}
        />
      )}
    </Container>
  );
}
